import { el } from './dom';
import { activateLicense, type LicensePayload } from '../lib/license';

export type LicenseModalProps = {
  onActivated: (payload: LicensePayload) => void;
  onClose: () => void;
};

export const LicenseModal = (props: LicenseModalProps): HTMLElement => {
  const root = el(`
    <div class="modal-overlay" id="license-modal" role="dialog" aria-modal="true">
      <div class="modal">
        <div class="section-label mono">
          <span class="label">enter license key</span>
          <span class="meta">we email it after Stripe checkout</span>
        </div>
        <h3 class="modal-title">Activate pro</h3>
        <p class="modal-body">Paste the license key we emailed you. We verify it offline against the public key bundled in this app — no server contact.</p>
        <textarea class="license-input" rows="5" placeholder="payload.signature" spellcheck="false"></textarea>
        <div class="license-error mono" hidden></div>
        <div class="modal-actions">
          <button type="button" class="modal-skip" data-action="close">close</button>
          <button type="button" class="modal-accept" data-action="activate">activate →</button>
        </div>
      </div>
    </div>
  `);

  const input = root.querySelector<HTMLTextAreaElement>('.license-input');
  const error = root.querySelector<HTMLElement>('.license-error');
  const close = root.querySelector<HTMLButtonElement>('[data-action="close"]');
  const activate = root.querySelector<HTMLButtonElement>('[data-action="activate"]');

  const showError = (msg: string): void => {
    if (!error) return;
    error.hidden = false;
    error.textContent = msg;
  };
  const clearError = (): void => {
    if (!error) return;
    error.hidden = true;
    error.textContent = '';
  };

  close?.addEventListener('click', () => {
    root.remove();
    props.onClose();
  });

  activate?.addEventListener('click', () => {
    void (async (): Promise<void> => {
      const value = input?.value.trim() ?? '';
      if (!value) {
        showError('paste a license key first');
        return;
      }
      clearError();
      activate.disabled = true;
      const result = await activateLicense(value);
      activate.disabled = false;
      if (!result.ok) {
        if (result.reason === 'invalid') {
          showError("that key didn't verify — check that you copied the whole thing");
        } else if (result.reason === 'limit-reached') {
          showError(
            `this license is already activated on ${String(result.max)} devices. ` +
              `email contact@anomia.app if that's a mistake.`,
          );
        } else {
          showError(
            "couldn't reach the activation server — check your connection and try again",
          );
        }
        return;
      }
      root.remove();
      props.onActivated(result.payload);
    })();
  });

  return root;
};
